import { useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Lock, X } from "lucide-react";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { REIMBURSEMENT_MILESTONE_LABELS } from "../../utils/reimbursementMilestoneStatus";

export interface ReimbursementSectionSubmitConfirmProps {
  pendingMilestoneId: number | null;
  onConfirm: (milestoneId: number) => void;
  onDismiss: () => void;
}

export function ReimbursementSectionSubmitConfirm({
  pendingMilestoneId,
  onConfirm,
  onDismiss,
}: ReimbursementSectionSubmitConfirmProps) {
  const isOpen = pendingMilestoneId !== null;

  const pendingMilestoneLabel =
    pendingMilestoneId != null &&
    (pendingMilestoneId === 1 ||
      pendingMilestoneId === 2 ||
      pendingMilestoneId === 3)
      ? REIMBURSEMENT_MILESTONE_LABELS[pendingMilestoneId]
      : "Section";

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onDismiss]);

  const handleConfirm = () => {
    const milestoneId = pendingMilestoneId;
    onDismiss();
    if (milestoneId != null) {
      onConfirm(milestoneId);
    }
  };

  if (!isOpen) return null;

  const title = `Submit ${pendingMilestoneLabel}`;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex animate-fadeIn items-center justify-center bg-black/50 p-4"
      data-testid="reimbursement-section-submit-confirm"
      onClick={onDismiss}
    >
      <div
        className="w-full max-w-[540px] animate-slideUp overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reimbursement-section-submit-confirm-title"
        aria-describedby="reimbursement-section-submit-confirm-description"
      >
        <div className="flex items-center justify-between border-b border-[#e2e8f0] bg-[#f0f4ff] px-6 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-blue-600 shadow-sm">
              <Lock size={20} aria-hidden="true" strokeWidth={2.25} />
            </div>
            <h2
              id="reimbursement-section-submit-confirm-title"
              className="text-xl font-bold text-[#173f7a]"
            >
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className="text-slate-800 transition-colors hover:text-gray-900"
            aria-label="Close"
          >
            <X size={22} aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-5 p-6" id="reimbursement-section-submit-confirm-description">
          <h3 className="text-lg font-bold text-[#173f7a]">
            Are you ready to submit the {pendingMilestoneLabel} section?
          </h3>

          <div className="flex items-start gap-3 rounded-lg border-2 border-[#ffe5b4] bg-[#fff9e6] p-5">
            <div
              className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#ffedd5]"
              aria-hidden="true"
            >
              <AlertTriangle className="text-[#ea580c]" size={20} strokeWidth={2.25} />
            </div>
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-bold text-[#ea580c]">
                This action cannot be undone.
              </p>
              <p className="text-sm leading-relaxed text-[#6b7280]">
                Once submitted, your {pendingMilestoneLabel} section will be locked and
                cannot be edited. Please review your details before continuing.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-gray-200 bg-white px-6 py-4 sm:flex-row sm:items-center sm:justify-end sm:rounded-b-xl">
          <button
            type="button"
            onClick={onDismiss}
            data-testid="reimbursement-section-submit-confirm-no"
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50 sm:w-auto"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            data-testid="reimbursement-section-submit-confirm-yes"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 sm:w-auto"
          >
            <Lock size={16} aria-hidden="true" />
            Submit &amp; Lock
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
